import { useCallback, useEffect } from "react";
import {
  APIProvider,
  Map,
  AdvancedMarker,
  useMap,
  type MapMouseEvent,
} from "@vis.gl/react-google-maps";
import { SearchBox, type SelectedPlace } from "./SearchBox";
import { API_BASE_URL } from "../../config/api";

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as
  | string
  | undefined;
const MAP_ID =
  (import.meta.env.VITE_GOOGLE_MAPS_MAP_ID as string | undefined) ||
  "postnatalcare-admin-map";

async function reverseGeocode(
  lat: number,
  lng: number,
): Promise<SelectedPlace | null> {
  try {
    const res = await fetch(
      `${API_BASE_URL}/businesses/geocode?lat=${lat}&lng=${lng}`,
    );
    const json = await res.json();
    const result = json.results?.[0];
    if (!result) return null;
    const comps: { types: string[]; long_name: string }[] =
      result.address_components ?? [];
    const get = (type: string) =>
      comps.find((c) => c.types.includes(type))?.long_name ?? "";
    const address = result.formatted_address ?? "";
    return {
      lat,
      lng,
      displayName: address,
      address,
      city: get("locality") || get("administrative_area_level_2"),
      district:
        get("administrative_area_level_3") ||
        get("administrative_area_level_2"),
      pincode: get("postal_code"),
    };
  } catch {
    return null;
  }
}

export interface LatLng {
  lat: number;
  lng: number;
}

interface LocationPickerProps {
  value: LatLng;
  onChange: (v: LatLng) => void;
  onPlaceSelected?: (place: SelectedPlace) => void;
}

const MapRecenter = ({ center }: { center: LatLng }) => {
  const map = useMap();
  useEffect(() => {
    if (!map) return;
    map.panTo(center);
    const zoom = map.getZoom() ?? 0;
    if (zoom < 13) map.setZoom(14);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [center.lat, center.lng, map]);
  return null;
};

const InnerPicker = ({
  value,
  onChange,
  onPlaceSelected,
}: LocationPickerProps) => {
  const handleSelect = useCallback(
    (place: SelectedPlace) => {
      onChange({ lat: place.lat, lng: place.lng });
      onPlaceSelected?.(place);
    },
    [onChange, onPlaceSelected],
  );

  const handleMapClick = useCallback(
    async (e: MapMouseEvent) => {
      const latLng = e.detail.latLng;
      if (!latLng) return;
      const lat = latLng.lat;
      const lng = latLng.lng;
      onChange({ lat, lng });
      const place = await reverseGeocode(lat, lng);
      if (place) onPlaceSelected?.(place);
    },
    [onChange, onPlaceSelected],
  );

  const handleDragEnd = useCallback(
    async (e: google.maps.MapMouseEvent) => {
      if (!e.latLng) return;
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      onChange({ lat, lng });
      const place = await reverseGeocode(lat, lng);
      if (place) onPlaceSelected?.(place);
    },
    [onChange, onPlaceSelected],
  );

  return (
    <div className="space-y-2">
      <SearchBox onSelect={handleSelect} />
      <div className="h-72 rounded-card overflow-hidden border border-border">
        <Map
          defaultCenter={value}
          defaultZoom={13}
          mapId={MAP_ID}
          gestureHandling="greedy"
          disableDefaultUI={false}
          style={{ width: "100%", height: "100%" }}
          onClick={handleMapClick}
        >
          <MapRecenter center={value} />
          <AdvancedMarker
            position={value}
            draggable
            onDragEnd={handleDragEnd}
          />
        </Map>
      </div>
      <p className="text-xs text-muted-foreground">
        Lat: {value.lat.toFixed(6)}, Lng: {value.lng.toFixed(6)} — click map or
        drag marker to adjust.
      </p>
    </div>
  );
};

export const LocationPicker = (props: LocationPickerProps) => {
  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <div className="h-64 rounded-card border border-dashed border-border flex items-center justify-center text-sm text-muted-foreground p-4 text-center">
        Map unavailable — set{" "}
        <code className="mx-1">VITE_GOOGLE_MAPS_API_KEY</code> in{" "}
        <code className="mx-1">apps/admin-panel/.env</code> and restart the dev
        server.
      </div>
    );
  }
  return (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY} libraries={["places"]}>
      <InnerPicker {...props} />
    </APIProvider>
  );
};

export default LocationPicker;
